// Транспорт и хуки инженерного блока (зеркало портала 73). ТОЛЬКО ЧТЕНИЕ.
//
// Отдельный файл, а не api/client.ts + api/hooks.ts: там всё построено на эмуляции Bitrix REST
// (POST /api/bx с method/params), а здесь обычные GET-ручки ядра (/p73/*) со своими фильтрами.
// Смешивать их — значит однажды передать ListParams туда, где ждут строку запроса.
//
// Инженеры продолжают работать на портале 73: раздел в CRM — обзор. Ни одной записи здесь нет
// и появиться не должно (см. шапку p73_api.py).

import { useQuery } from '@tanstack/react-query'

import { CoreUnsupported, Unauthorized } from './client'

const БАЗА = '/api/p73'

/** Тот же разбор ответа, что в api/client.ts: 401 → на логин, остальное — ApiError. */
async function запрос<T>(путь: string): Promise<T> {
  const res = await fetch(БАЗА + путь, { credentials: 'same-origin' })
  if (res.status === 401) throw new Unauthorized()
  const текст = await res.text()
  const данные: unknown = текст ? JSON.parse(текст) : {}
  if (!res.ok) {
    const rec = (данные || {}) as Record<string, unknown>
    // 400 от ядра — это «такой фильтр обслужить нельзя». Пустая таблица здесь читалась бы
    // как «ничего не найдено», поэтому поднимаем тот же тип, что и у остальных разделов:
    // QueryState печатает по нему честное объяснение вместо пустого списка.
    if (res.status === 400) throw new CoreUnsupported(String(rec.why ?? rec.error ?? ''))
    const msg = (rec.error as string) || (rec.detail as string) || `HTTP ${res.status}`
    throw new Error(msg)
  }
  return данные as T
}

// ─────────── формы ответов ядра (см. p73_api.py) ───────────

/** Мостик к карточке CRM: локальный id портала + номер сущности Bitrix. */
export interface Мостик {
  d1_id: number | null
  bitrix_id: number | null
}

export interface СудноP73 extends Мостик {
  name: string | null
  imo: string | null
  mmsi: string | null
}

export interface ЗаявкаP73 extends Мостик {
  title: string | null
  status: string | null
  location: string | null
}

export interface ИнженерP73 extends Мостик {
  name: string | null
  /** Уровень (0 Стажёр … 4 Мастер). Ненулевой у 3 инженеров из 136 (на 28.07.2026):
   *  «не проставлен» записано нулём, а не null, так что 0 читать как «Стажёр» нельзя. */
  level: number | null
}

interface Мостики {
  vessel: СудноP73 | null
  deal: ЗаявкаP73 | null
  engineer: ИнженерP73 | null
}

export interface ВерификацияСтрока extends Мостики {
  d1_id: number
  type: 'short' | 'full' | string
  status: string
  fill_pct: number | null
  defects_found: number | null
  initiative: boolean | null
  points_base: number | null
  points_total: number | null
  reject_reason: string | null
  created_at: string | null
  updated_at: string | null
  submitted_at: string | null
  reviewed_at: string | null
  reviewer_name: string | null
  vessel_d1_id: number | null
  deal_d1_id: number | null
  engineer_d1_id: number | null
  /** Число фотографий по реестру ядра — считается запросом, не берётся из строки. */
  photos_total: number
  /** Счётчик самого портала. Ноль в 141 строке из 169 (на 28.07.2026), причём ноль здесь
   *  значит «портал не считал», а не «фотографий нет». Опираться на него нельзя. */
  photo_count_portal: number | null
}

export interface ПолеФормы {
  key: string
  label: string
  type: string
  value: unknown
  opts: unknown
  /** Код поля Bitrix: видно, что уедет в сделку при утверждении. Часто null. */
  uf: string | null
}

export interface РазделЧекЛиста {
  key: string
  title: string
  fields: ПолеФормы[]
  filled: number
  absent: boolean
}

export interface ПунктПроверки {
  key: string
  label: string
  value: string | null
  note: string
}

export interface БлокЧекЛиста {
  key: string
  instances: Array<{ label: string; checks: ПунктПроверки[] }>
}

export interface ФотоP73 {
  d1_id: number
  label: string | null
  r2_key: string | null
  size_bytes: number | null
  created_at: string | null
  section_key?: string | null
}

export interface ВерификацияКарточка extends ВерификацияСтрока {
  header: Record<string, string | null>
  sections: РазделЧекЛиста[]
  checklist: БлокЧекЛиста[]
  notes: string
  photos: ФотоP73[]
  meta: {
    absent_sections: Record<string, boolean>
    absent_blocks: Record<string, boolean>
    absent_instances: Record<string, Record<string, boolean>>
    hidden_groups: Record<string, boolean>
    river_register: boolean
    full_v3: boolean
    general_notes: string
    unknown_keys: string[]
  }
}

export interface ОтчётСтрока extends Мостики {
  d1_id: number
  status: string
  fill_pct: number | null
  points_base: number | null
  points_total: number | null
  reject_reason: string | null
  created_at: string | null
  updated_at: string | null
  submitted_at: string | null
  reviewed_at: string | null
  reviewer_name: string | null
  vessel_d1_id: number | null
  deal_d1_id: number | null
  engineer_d1_id: number | null
  photos_total: number
  photo_count_portal: number | null
}

export interface ЭтапОтчёта {
  code: string
  title: string
  repeat: boolean
  photos: number
  items?: Array<{ key: string; value: unknown }>
  blocks?: Array<{
    label: string
    seeded: boolean
    photos: number
    items: Array<{ key: string; value: unknown }>
  }>
}

export interface ОтчётКарточка extends ОтчётСтрока {
  stages: ЭтапОтчёта[]
  photos: ФотоP73[]
  meta: { unknown_keys: string[]; photos_other: Record<string, number> }
}

export interface АктСтрока {
  d1_id: number
  type: string
  number: string | null
  vessel_name: string | null
  imo: string | null
  mmsi: string | null
  /** Поле «Сделка (необязательно)» из формы портала. Пусто во ВСЕХ строках. */
  deal_number: string | null
  act_date: string | null
  place: string | null
  units: number | null
  status: string
  created_by_d1_id: number | null
  engineer_name: string | null
  created_at: string | null
  updated_at: string | null
  has_scan: boolean
  from_archive: boolean
  vessel: СудноP73 | null
  engineer: ИнженерP73 | null
}

export interface ФормаАкта {
  header: Record<string, unknown> | null
  rows: Record<string, unknown> | null
  checks: Record<string, string> | null
  /** Пункты формы 6.3.22.2 РС — только у актов r6322, разобранных из архива. */
  items: Record<string, string> | null
  key_dates: Array<{ label: string; value: string }> | null
  archived_from: { file?: string; task?: number } | null
  prefilled_from: { id?: number; date?: string } | null
  note: string | null
  parse_full_fail: unknown
  unknown_keys: string[]
}

export interface АктКарточка extends АктСтрока {
  form: ФормаАкта
  file_key: string | null
  files: Array<{
    d1_id: number
    task_id: number | null
    file_name: string | null
    act_type: string | null
    vessel_guess: string | null
    year: string | null
    att_id: number | null
    status: string | null
  }>
  task: {
    task_id: number
    title: string | null
    kw: string | null
    created: string | null
    status: string | null
    comments_scanned: number | null
  } | null
  no_deal_link: boolean
}

export interface СтраницаP73<T> {
  rows: T[]
  total: number
  next: number | null
  limit: number
  start: number
  no_deal_link?: boolean
}

export interface СправочникиP73 {
  engineers: Array<{
    d1_id: number
    full_name: string | null
    username: string | null
    role: string | null
    level: number | null
    is_active: boolean | null
    bitrix_user_id: number | null
  }>
  verification_status: Record<string, number>
  verification_type: Record<string, number>
  job_report_status: Record<string, number>
  act_type: Record<string, number>
  act_status: Record<string, number>
  counts: {
    verifications: number
    job_reports: number
    acts: number
    auto_verifications: number
    verification_photos: number
    job_report_photos: number
    acts_without_imo: number
    acts_with_deal: number
    verifications_without_photo_count: number
  }
  synced: { verifications: string | null }
  data_notes: Record<string, string>
}

// ─────────── сборка строки запроса ───────────
// Пустые значения не отправляем: ядро игнорирует неизвестные параметры, но пустой `status=`
// в ключе кэша плодил бы разные ключи на одинаковый запрос.

export type ФильтрыP73 = Record<string, string | number | boolean | undefined | null>

export function строкаЗапроса(ф: ФильтрыP73): string {
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(ф)) {
    if (v === undefined || v === null || v === '' || v === false) continue
    p.set(k, v === true ? '1' : String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

// ─────────── хуки ───────────
// staleTime большой: зеркало обновляется синком раз в несколько минут, чаще спрашивать
// нечего. placeholderData — чтобы страница не мигала пустотой при смене фильтра.

const общее = { retry: 1, staleTime: 60_000 } as const

export const useP73Refs = () =>
  useQuery({
    queryKey: ['p73', 'refs'],
    queryFn: () => запрос<СправочникиP73>('/refs'),
    staleTime: 10 * 60_000,
    retry: 1,
  })

export const useВерификации = (ф: ФильтрыP73) =>
  useQuery({
    queryKey: ['p73', 'verifications', ф],
    queryFn: () => запрос<СтраницаP73<ВерификацияСтрока>>('/verifications' + строкаЗапроса(ф)),
    placeholderData: (prev) => prev,
    ...общее,
  })

export const useВерификация = (id: string | undefined) =>
  useQuery({
    queryKey: ['p73', 'verification', id],
    queryFn: () => запрос<{ row: ВерификацияКарточка }>(`/verification?id=${encodeURIComponent(id!)}`)
      .then((r) => r.row),
    enabled: !!id,
    ...общее,
  })

export const useОтчёты = (ф: ФильтрыP73) =>
  useQuery({
    queryKey: ['p73', 'job-reports', ф],
    queryFn: () => запрос<СтраницаP73<ОтчётСтрока>>('/job-reports' + строкаЗапроса(ф)),
    placeholderData: (prev) => prev,
    ...общее,
  })

export const useОтчёт = (id: string | undefined) =>
  useQuery({
    queryKey: ['p73', 'job-report', id],
    queryFn: () => запрос<{ row: ОтчётКарточка }>(`/job-report?id=${encodeURIComponent(id!)}`)
      .then((r) => r.row),
    enabled: !!id,
    ...общее,
  })

export const useАкты = (ф: ФильтрыP73) =>
  useQuery({
    queryKey: ['p73', 'acts', ф],
    queryFn: () => запрос<СтраницаP73<АктСтрока>>('/acts' + строкаЗапроса(ф)),
    placeholderData: (prev) => prev,
    ...общее,
  })

export const useАкт = (id: string | undefined) =>
  useQuery({
    queryKey: ['p73', 'act', id],
    queryFn: () => запрос<{ row: АктКарточка }>(`/act?id=${encodeURIComponent(id!)}`)
      .then((r) => r.row),
    enabled: !!id,
    ...общее,
  })

// ─────────── сами файлы: фотографии и сканы ───────────
// ПОЧЕМУ ФАЙЛ ЕДЕТ В JSON, А НЕ ПО АДРЕСУ КАРТИНКИ. Адреса у фотографии нет вовсе — и это
// не обход ограничения, а требование. Всё, что имеет адрес, рано или поздно оказывается
// в пересланном сообщении и открывается без сессии: ровно так вышло со ссылками на чеки
// Яндекс.Такси, из-за чего их не перенесли в зеркало (ПЛАН_остаток.md, В7.3). Здесь
// браузер получает БАЙТЫ по обычному запросу с сессионной кукой и собирает data:-адрес
// сам. Скопировать «ссылку на изображение» и отправить её в чат нельзя: скопируются сами
// байты, а не право их получить.
//
// Цена — +33 % объёма на base64 и файл целиком в памяти. При среднем снимке 400 КБ это
// приемлемо, а качать их пачкой мы и не собираемся: запрос идёт на одну фотографию, когда
// человек её открыл (`enabled`).

/** Ответ ручек /p73/verification-photo, /job-report-photo, /autover-photo, /act-scan. */
export interface ФайлP73 {
  d1_id: number
  parent_id: number | null
  label: string | null
  section_key: string | null
  /** Тип решило ЯДРО по своему списку расширений, а не чужое хранилище: см. p73_photo.py. */
  content_type: string
  /** true — это картинка, её можно показать. false — только «скачать». */
  inline: boolean
  size: number
  created_at: string | null
  b64: string
}

/** Какой из четырёх реестров спрашиваем. Путь = раздел: право проверяет BFF по пути. */
export type ВидФайлаP73 = 'verification-photo' | 'job-report-photo' | 'autover-photo' | 'act-scan'

export const useФайлP73 = (вид: ВидФайлаP73, id: number | string | undefined, включён = true) =>
  useQuery({
    queryKey: ['p73', 'file', вид, id],
    queryFn: () => запрос<{ file: ФайлP73 }>(`/${вид}?id=${encodeURIComponent(String(id))}`)
      .then((r) => r.file),
    enabled: включён && id !== undefined && id !== null && id !== '',
    // Файл неизменен: ключ в R2 содержит момент загрузки, перезаписи объекта у портала нет.
    // Час в кэше избавляет от повторного скачивания при листании карточки туда-обратно.
    staleTime: 60 * 60_000,
    // Второй заход за 12-мегабайтным PDF по таймауту хуже, чем честная ошибка.
    retry: 0,
  })

/** data:-адрес из ответа ядра. Готового адреса у файла нет — он собирается здесь и сейчас. */
export const адресФайла = (ф: ФайлP73): string => `data:${ф.content_type};base64,${ф.b64}`

// ═══════════ «Верификация-3 Автоматика»: ручка ядра была, экрана не было ═══════════
// Отдельная форма портала 73 (`autover.js`), 39 записей. От обычной верификации отличается
// главным: в Bitrix она НЕ уходит вовсе — решение от 12.06.2026 записано в самом портале
// (autover.js:2). Фотографий у сервиса нет ни одной, поэтому реестр `p73_autover_photos`
// пуст, и экран обязан сказать это словами, а не показать «0» без пояснения.

export interface АвтоверификацияСтрока extends Мостики {
  d1_id: number
  /** В данных сегодня одно значение — `draft`: ни одна форма не отправлена. */
  status: string
  fill_pct: number | null
  created_at: string | null
  updated_at: string | null
  vessel_d1_id: number | null
  deal_d1_id: number | null
  engineer_d1_id: number | null
  /** Считается по реестру `p73_autover_photos`. Он пуст — значит везде ноль. */
  photos_total: number
}

export const useАвтоверификации = (ф: ФильтрыP73) =>
  useQuery({
    queryKey: ['p73', 'auto-verifications', ф],
    queryFn: () => запрос<СтраницаP73<АвтоверификацияСтрока>>(
      '/auto-verifications' + строкаЗапроса(ф)),
    placeholderData: (prev) => prev,
    ...общее,
  })

// ═══════════════════ сводка «батарейки и сроки» (p73_equip_due.py) ═══════════════════
// Не список таблицы, а ВЫВОД поверх 2 630 актов (на 28.07.2026): последний акт каждого
// типа по судну, из него разобранные сроки. Пороги «скоро»/«в течение года» — в `thresholds`
// именно потому, что они НАШИ: в актах портала сроки просто записаны и ни с чем не
// сравниваются. Экран обязан это показать.

/** Что за срок: 'срок' — успеть к дате, 'факт' — дата уже случившегося, 'неясно' — из подписи не видно. */
export type НаправлениеСрока = 'срок' | 'факт' | 'неясно'

export type СостояниеСрока =
  'overdue' | 'soon' | 'year' | 'ok' | 'fact' | 'unclear' | 'nodate'

export interface СрокСтрока {
  vessel_name: string | null
  imo: string | null
  mmsi: string | null
  act_d1_id: number
  act_type: string
  act_date: string | null
  engineer_name: string | null
  /** Подпись срока: из бланка портала либо из key_dates, разобранных ИИ на портале. */
  label: string
  kind: string
  direction: НаправлениеСрока
  /** Как записано в акте: «06.2029», «H 20E, 12.2027». Показываем как есть — это первоисточник. */
  value: string
  due: string | null
  /** До какого знака известна дата: месяц у большинства сроков, день — редкость. */
  precision: 'day' | 'month' | 'year' | null
  status: СостояниеСрока
  days_left: number | null
  note?: string
  source: string
}

export interface ГраницыСроков {
  acts_all: number
  acts_live: number
  acts_done: number
  acts_draft: number
  act_from: string | null
  act_to: string | null
  vessels_known: number
  synced_at: string | null
  last_sync_at: string | null
  last_count: number | null
}

export interface СводкаСроков extends СтраницаP73<СрокСтрока> {
  /** День, на который посчитана просрочка. Считает ЯДРО — иначе часовой пояс браузера сдвинет. */
  as_of: string
  summary: Record<СостояниеСрока, number>
  vessels: number
  act_types: Record<string, number>
  kinds: Record<string, string>
  thresholds: { soon_days: number; year_days: number }
  bounds: ГраницыСроков
  data_notes: Record<string, string>
}

export const useСрокиОборудования = (ф: ФильтрыP73) =>
  useQuery({
    queryKey: ['p73', 'equip-due', ф],
    queryFn: () => запрос<СводкаСроков>('/equip-due' + строкаЗапроса(ф)),
    placeholderData: (prev) => prev,
    ...общее,
  })

// ═══════════════ возраст зеркала: подпись под печатным листом ═══════════════
// Печатный лист (lib/print73.ts) обязан сказать на бумаге, когда зеркало последний раз
// сверялось с порталом 73. Своей ручки под это нет и завести её нельзя: маршруты ядра
// объявлены в p73_api.py, а белый список путей — в web/bff/main.py, и оба файла правит
// только основная сессия. Поэтому возраст едет параметром уже подключённого пути
// `/p73/equip-due` (см. `свежесть` в p73_equip_due.py), и право на него ровно то же,
// что на раздел актов.

export interface СвежестьСтрока73 {
  entity: string
  table: string
  /** Человеческая подпись: «акты на оборудование», а не «p73_equip_acts». */
  label: string
  last_sync_at: string | null
  /** null — синк по этой сущности не отмечался НИ РАЗУ. Это не ноль минут. */
  age_min: number | null
  /** Счётчик последнего прогона синка. Расходится с rows_now — синк отстал от факта. */
  last_count: number | null
  mirror_at: string | null
  rows_now: number | null
}

export interface СвежестьЗеркала73 {
  freshness: СвежестьСтрока73[]
  /** Самое старое из участвующих. Лист не свежее своей самой старой части. */
  oldest_min: number | null
  unknown: string[]
  server_now: string | null
  note: string
}

export const useСвежестьЗеркала73 = (включён = true) =>
  useQuery({
    queryKey: ['p73', 'freshness'],
    queryFn: () => запрос<СвежестьЗеркала73>('/equip-due?only=freshness'),
    enabled: включён,
    // Полминуты: число уезжает на бумагу, и «5 минут назад» на листе, собранном час
    // спустя, было бы враньём с нашей стороны, а не отставанием синка.
    staleTime: 30_000,
    retry: 1,
  })
